import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { CampaignProgress } from "@/components/site/CampaignProgress";
import { SectionHead, DoodlePhoto, DoodleBand, LightSection } from "@/components/site/Shared";
import { ScheduleBlock } from "@/components/site/ScheduleBlock";
import { DonorWall } from "@/components/site/DonorWall";
import { SeriesButton } from "@/components/site/Header";
import { Newsletter } from "@/components/site/Newsletter";
import { VideoBox } from "@/components/site/VideoBox";
import { PoemOfTheMonth } from "@/components/site/PoemOfTheMonth";

const WAYS = [
  { title: "Poetry Club", to: "/poetry-club", text: "Free registration, open mics and a room that listens.", color: "bg-aqua" },
  { title: "Literacy programs", to: "/impact", text: "Reading circles, books and journals for every age.", color: "bg-quartz" },
  { title: "Writing workshops", to: "/support", text: "Generative workshops led by working poets.", color: "bg-mustard" },
  { title: "Def Street Festival", to: "/def-street", text: "Our annual poetry festival — the whole block on the mic.", color: "bg-tang" },
  { title: "Community", to: "/community", text: "Volunteers, featured poets, photographs and newsletter.", color: "bg-lav" },
];

const Hero = ({ s }) => (
  <section data-testid="home-hero">
    <DoodlePhoto src={s.images?.hero} alt="Poets reading in front of a mural" className="h-[340px] sm:h-[480px] lg:h-[560px]" />
    <div className="container-mas py-10 sm:py-14 flex flex-col md:flex-row md:items-center md:justify-end gap-6 md:gap-10">
      <div className="md:text-right rise">
        <p className="eyebrow-dark mb-2">{s.hero_eyebrow}</p>
        <h1 className="font-sans font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1]" data-testid="home-hero-title">{s.hero_title}</h1>
        <p className="mt-3 text-shell/75 font-light text-base sm:text-lg">{s.hero_subtitle}</p>
      </div>
      <div className="flex flex-wrap gap-3 shrink-0 rise" style={{ animationDelay: ".1s" }}>
        <Link to="/donate" className="btn-terra !px-8" data-testid="donate-now-hero-button">Donate <ArrowRight className="w-4 h-4" /></Link>
        <Link to="/poetry-club" className="btn-line" data-testid="hero-join-club-button">Join Poetry Club</Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  const { settings: s } = useSite() || {};
  if (!s) return <div className="h-[80vh]" />;
  return (
    <div data-testid="home-page">
      <Hero s={s} />

      <section className="container-mas py-16 sm:py-20" data-testid="home-mission">
        <h2 className="h-section mb-10">About us</h2>
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
          <DoodlePhoto src={s.images?.story} alt="Spoken word performer" doodles={false} className="aspect-[4/3]" />
          <div className="space-y-5 text-shell/80 font-light leading-relaxed">
            <p className="text-lg sm:text-xl text-shell font-normal">{s.mission}</p>
            <p className="whitespace-pre-line">{s.story}</p>
            <Link to="/community" className="btn-line" data-testid="home-story-community-link">Meet the community <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      <DoodleBand />

      <section className="container-mas py-16 sm:py-20" data-testid="home-schedule">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8">
          <SectionHead eyebrow={`${s.events_per_year}+ free events a year`} title="The schedule" />
          <Link to="/poetry-club#join" className="btn-terra" data-testid="home-schedule-register-link">Register free</Link>
        </div>
        <ScheduleBlock limit={8} />
      </section>

      <LightSection testid="home-campaign">
        <div className="container-mas py-16 sm:py-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <SectionHead light eyebrow={`the ${s.campaign_years}-year campaign`} title={s.campaign_title} intro={s.campaign_blurb} />
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/donate" className="btn-terra" data-testid="home-campaign-donate-button">Donate now</Link>
              <Link to="/donate" className="btn-ink" data-testid="home-campaign-monthly-button">Give monthly</Link>
              <Link to="/impact" className="btn-ink" data-testid="home-campaign-impact-link">Where funds go</Link>
            </div>
          </div>
          <div className="lg:col-span-6 shadow-[10px_10px_0_#1D1D1B]"><CampaignProgress testid="home-campaign-progress" /></div>
        </div>
      </LightSection>

      <section className="container-mas py-16 sm:py-20 grid md:grid-cols-2 gap-10 items-start" data-testid="home-programs">
        <div>
          <h2 className="h-section mb-8">Ways in</h2>
          <ol className="space-y-1">
            {WAYS.map((w, i) => (
              <li key={w.title}>
                <Link to={w.to} className="group flex gap-6 py-4 border-b border-white/10 hover:border-tang transition-colors" data-testid={`home-program-${w.title.toLowerCase().replace(/\s/g, "-")}`}>
                  <span className="font-mono text-tang w-5 pt-1">{i + 1}</span>
                  <span className="flex-1">
                    <span className="block text-xl sm:text-2xl font-medium group-hover:text-tang transition-colors">{w.title}</span>
                    <span className="block text-sm text-shell/60 font-light mt-1">{w.text}</span>
                  </span>
                  <ArrowRight className="w-5 h-5 mt-1.5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform]" />
                </Link>
              </li>
            ))}
          </ol>
        </div>
        <div className="md:mt-20">
          <VideoBox url={s.ways_video_url} className="shadow-[10px_10px_0_#ED773C]" />
        </div>
      </section>

      <LightSection testid="home-donor-wall">
        <div className="container-mas py-16 sm:py-20">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
            <SectionHead light eyebrow="donor wall" title="The names keeping the mic on" />
            <Link to="/donate" className="btn-ink" data-testid="home-donor-wall-link">Add your name</Link>
          </div>
          <DonorWall limit={6} showControls={false} />
        </div>
      </LightSection>

      <section className="bg-lav text-ink" data-testid="home-series">
        <div className="container-mas py-16 sm:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3">mas series</p>
            <h2 className="h-section">Can't make it in person? Watch the MAS Series.</h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <SeriesButton className="inline-flex items-center gap-2 rounded-full bg-ink text-shell px-8 py-4 font-semibold hover:bg-passion transition-colors" testid="home-watch-mas-series-button" />
          </div>
        </div>
      </section>

      <PoemOfTheMonth s={s} />

      <section className="container-mas py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center" data-testid="home-newsletter">
        <SectionHead eyebrow="stay in the loop" title="Never miss an open mic" />
        <Newsletter dark testid="home-newsletter" />
      </section>
    </div>
  );
}
